import { Paper } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import type { ShiftInspection } from "../../models/Inspection";
import { useTableData } from "../hooks/useTableData";

interface TestShackInspectionsTableProps {
	sheetRoute: string;
	inspections: ShiftInspection[];
	isLoading: boolean;
	highlightedProfileInspectionId: number | null;
	onDelete: (inspection: ShiftInspection) => void;
}

export const TestShackInspectionsTable = ({
	sheetRoute,
	inspections,
	isLoading,
	highlightedProfileInspectionId,
	onDelete,
}: TestShackInspectionsTableProps) => {
	const navigate = useNavigate();
	const gridApi = useRef<GridApi<ShiftInspection> | null>(null);

	const openInspection = (inspection: ShiftInspection) =>
		navigate(`${sheetRoute}/${inspection.ProfileInspectionId}`);
	const { columnDefs } = useTableData({
		openInspection,
		deleteInspection: onDelete,
	});

	const revealHighlightedRow = () => {
		if (highlightedProfileInspectionId === null) return;

		const node = gridApi.current?.getRowNode(
			String(highlightedProfileInspectionId),
		);
		if (node) gridApi.current?.ensureNodeVisible(node, "middle");
	};

	return (
		<Paper
			data-test-id="manager-inspections-table"
			elevation={2}
			sx={{
				height: "calc(100vh - 340px)",
				width: "100%",
				[`& .ag-row.${"test-shack-highlighted-row"}`]: {
					backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.16),
				},
			}}
		>
			<AgGridReact<ShiftInspection>
				loading={isLoading}
				rowData={inspections}
				columnDefs={columnDefs}
				defaultColDef={{
					editable: false,
					filter: false,
					resizable: false,
					sortable: true,
					suppressHeaderMenuButton: true,
				}}
				rowHeight={34}
				headerHeight={30}
				getRowId={({ data }) => String(data.ProfileInspectionId)}
				rowClassRules={{
					["test-shack-highlighted-row"]: ({ data }) =>
						data?.ProfileInspectionId === highlightedProfileInspectionId,
				}}
				onGridReady={(event) => {
					gridApi.current = event.api;
				}}
				onFirstDataRendered={revealHighlightedRow}
				onRowDataUpdated={revealHighlightedRow}
				loadingOverlayComponent={CenteredLazyLoading}
				overlayNoRowsTemplate={"No samples were taken"}
				onRowDoubleClicked={({ data }) => data && openInspection(data)}
			/>
		</Paper>
	);
};
